// Product Details Page Component
// Displays detailed information about a product including:
// - Basic product info (name, description, SKU)
// - Images
// - Additional components (for advanced products)
// - Organizational metadata (categories, collections, tags)
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { useLanguage } from '@/context/LanguageContext'
import { PageContainer } from '@/components/shared/page-container'
import { PageContent } from '@/components/shared/page-content'
import { HeaderSection } from '@/components/shared/header-section'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { TableCard } from '@/components/shared/table-card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import ProductForm from './ProductForm'
import { SofaFormsTable } from './SofaFormsTable'
import useOpenDrawer from '@/hooks/useOpenDrawer'
import { AdditionalComponentsCard } from './AdditionalComponentsCard'
import { PackageDimensionsCard } from './PackageDimensionsCard'
import { FabricCombinationsCard } from './FabricCombinationsCard'
import { ProductPricelistsCard } from './ProductPricelistsCard'
import { ProductFeaturesCard } from './ProductFeaturesCard'
import { ProductAttributesCard } from './ProductAttributesCard'
import { LinkedProductsCard } from './LinkedProductsCard'
import { ImageUpload } from '@/components/shared/inputComponents/ImageUpload'
import { Edit, Save, X, MoveUp, MoveDown, Pencil, Star } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  GET_PRODUCT_DETAILS,
  UPDATE_PRODUCT_VISIBILITY,
} from '../productQueries'
import {
  UPDATE_PRODUCT_IMAGES,
  UPDATE_FURNITURE_TYPE,
  UPDATE_SINGLE_PRODUCT_IMAGES,
  SET_CATEGORY_PHOTO,
} from '../productMutations'
import { FURNITURE_TYPE_LABELS, FURNITURE_TYPES } from '../constants'
import { CategoryEditModal } from './CategoryEditModal'
import { TagEditModal } from './TagEditModal'
import { CollectionEditModal } from './CollectionEditModal'

function ProductDetailsSkeleton() {
  return (
    <PageContainer>
      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-2 space-y-8">
          {/* Product Info Card Skeleton */}
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-4 w-1/2" />
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-3/4" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </CardContent>
          </Card>

          {/* Images Card Skeleton */}
          <Card>
            <CardHeader>
              <CardTitle>Images</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="w-full h-32 rounded-md" />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Additional Cards Skeleton */}
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-1/3" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        </div>

        <div>
          {/* Organize Card Skeleton */}
          <Card>
            <CardHeader>
              <CardTitle>Organize</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-6 w-12 rounded" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-4 w-3/4" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-1/4" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-16 rounded-full" />
                  <Skeleton className="h-6 w-20 rounded-full" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </PageContainer>
  )
}

export function ProductDetails() {
  const { id } = useParams()
  const openEditForm = useOpenDrawer(ProductForm)
  const { language } = useLanguage()
  const [isEditingImages, setIsEditingImages] = useState(false)
  const [imageUrls, setImageUrls] = useState<string[]>([])
  const [hasImageChanges, setHasImageChanges] = useState(false)
  const [showAllCollections, setShowAllCollections] = useState(false)
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false)
  const [isTagModalOpen, setIsTagModalOpen] = useState(false)
  const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false)

  const [updateVisibility] = useMutation(UPDATE_PRODUCT_VISIBILITY, {
    refetchQueries: [
      { query: GET_PRODUCT_DETAILS, variables: { id: Number(id) } },
    ],
  })

  const [updateFurnitureType] = useMutation(UPDATE_FURNITURE_TYPE, {
    refetchQueries: [
      { query: GET_PRODUCT_DETAILS, variables: { id: Number(id) } },
    ],
  })

  const refetchSpec = {
    refetchQueries: [{ query: GET_PRODUCT_DETAILS, variables: { id: Number(id) } }],
  }

  const [updateAdvancedImages] = useMutation(UPDATE_PRODUCT_IMAGES, {
    ...refetchSpec,
    onCompleted: () => {
      setIsEditingImages(false)
      setHasImageChanges(false)
    },
  })
  const [updateSingleImages] = useMutation(UPDATE_SINGLE_PRODUCT_IMAGES, {
    ...refetchSpec,
    onCompleted: () => {
      setIsEditingImages(false)
      setHasImageChanges(false)
    },
  })
  const [setCategoryPhoto] = useMutation(SET_CATEGORY_PHOTO, refetchSpec)

  const { data, loading, error } = useQuery(GET_PRODUCT_DETAILS, {
    variables: { id: Number(id), language: language.toUpperCase() },
    fetchPolicy: 'cache-and-network',
  })

  const productImagesSource =
    data?.findUniqueProductContainer?.advanced_product?.images
    ?? data?.findUniqueProductContainer?.single_product?.images
    ?? []

  // Initialize imageUrls from the product's images when data loads
  React.useEffect(() => {
    if (productImagesSource.length > 0) {
      const sortedImages = [...productImagesSource].sort(
        (a: any, b: any) => a.display_order - b.display_order
      )

      setImageUrls(sortedImages.map((img: any) => img.src))
    }
  }, [data])

  const handleStartEditingImages = () => {
    const sortedImages = [...productImagesSource].sort(
      (a: any, b: any) => a.display_order - b.display_order
    )

    setImageUrls(sortedImages.map((img: any) => img.src))
    setIsEditingImages(true)
    setHasImageChanges(false)
  }

  const handleCancelEditing = () => {
    setIsEditingImages(false)
    setHasImageChanges(false)
  }

  const handleSaveImages = async () => {
    const c = data?.findUniqueProductContainer
    if (!c) return
    try {
      if (c.advanced_product) {
        await updateAdvancedImages({ variables: { id: c.id, images: imageUrls } })
      } else if (c.single_product) {
        await updateSingleImages({ variables: { id: c.id, images: imageUrls } })
      }
    } catch (e) {
      console.error('Error updating images:', e)
    }
  }

  const handleImagesChange = (newImages: string[]) => {
    setImageUrls(newImages)
    setHasImageChanges(true)
  }

  const handleMoveImage = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === imageUrls.length - 1)
    ) {
      return
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1
    const newImages = [...imageUrls]
    const temp = newImages[index]
    newImages[index] = newImages[newIndex]
    newImages[newIndex] = temp

    setImageUrls(newImages)
    setHasImageChanges(true)
  }

  if (loading) return <ProductDetailsSkeleton />
  if (error) return <div>Error: {error.message}</div>

  const product = data?.findUniqueProductContainer
  if (!product) return <div>Product not found</div>

  const handleVisibilityChange = async (checked: boolean) => {
    try {
      await updateVisibility({
        variables: {
          id: Number(id),
          visible: checked,
        },
      })
    } catch (error) {
      console.error('Error updating visibility:', error)
    }
  }

  const handleFurnitureTypeChange = async (value: string) => {
    try {
      await updateFurnitureType({
        variables: { id: Number(id), advanced_product_type: value },
      })
    } catch (error) {
      console.error('Error updating furniture type:', error)
    }
  }

  const name =
    (product.single_product
      ? product.single_product.product_profiles.find(
          (profile: any) => profile.language.toLowerCase() === language
        )?.name || product.single_product.product_profiles[0]?.name
      : product.advanced_product?.advanced_product_profiles.find(
          (profile: any) => profile.language.toLowerCase() === language
        )?.name ||
        product.advanced_product?.advanced_product_profiles[0]?.name) || '-'

  const originalName =
    product.advanced_product?.advanced_product_profiles.find(
      (profile: any) => profile.language.toLowerCase() === language
    )?.original_product_name ||
    product.advanced_product?.advanced_product_profiles[0]
      ?.original_product_name

  const description = product.single_product
    ? product.single_product.product_profiles.find(
        (profile: any) => profile.language.toLowerCase() === language
      )?.description || product.single_product.product_profiles[0]?.description
    : product.advanced_product?.advanced_product_profiles.find(
        (profile: any) => profile.language.toLowerCase() === language
      )?.description ||
      product.advanced_product?.advanced_product_profiles[0]?.description

  // Get images and sort them by display_order
  const images = productImagesSource.length
    ? [...productImagesSource].sort(
        (a: any, b: any) => a.display_order - b.display_order
      )
    : []

  const categoryPhotoId =
    product.advanced_product?.category_photo_id
    ?? product.single_product?.category_photo_id
    ?? null

  const sofaForm = product.advanced_product?.sofa_forms

  return (
    <PageContent>
      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-2 space-y-8">
          <TableCard
            title={name}
            tooltipText={`Container ID: ${id}${
              product.advanced_product
                ? ` | Advanced Product ID: ${product.advanced_product.id} Reference: ${product.reference}`
                : ''
            }`}
            onEdit={() => openEditForm({ id, type: product.type })}
            data={[
              { label: 'Description', value: description },
              ...(product.single_product?.sku
                ? [{ label: 'SKU', value: product.single_product.sku }]
                : []),
              ...(originalName
                ? [{ label: 'Original Name', value: originalName }]
                : []),
              ...(product.advanced_product?.advanced_product_profiles.find(
                (profile: any) => profile.language.toLowerCase() === language
              )?.meta_information
                ? [
                    {
                      label: 'Permalink',
                      value:
                        product.advanced_product.advanced_product_profiles.find(
                          (profile: any) =>
                            profile.language.toLowerCase() === language
                        )?.meta_information.permalink || '-',
                    },
                    {
                      label: 'Meta Title',
                      value:
                        product.advanced_product.advanced_product_profiles.find(
                          (profile: any) =>
                            profile.language.toLowerCase() === language
                        )?.meta_information.meta_title || '-',
                    },
                    {
                      label: 'Meta Description',
                      value:
                        product.advanced_product.advanced_product_profiles.find(
                          (profile: any) =>
                            profile.language.toLowerCase() === language
                        )?.meta_information.meta_description || '-',
                    },
                  ]
                : []),
            ]}
          />

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle>Images</CardTitle>
              <div className="flex space-x-2">
                {!isEditingImages && (product.advanced_product || product.single_product) && (
                  <Button
                    onClick={handleStartEditingImages}
                    size="sm"
                    variant="outline"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </Button>
                )}
                {isEditingImages && (
                  <>
                    <Button
                      onClick={handleCancelEditing}
                      size="sm"
                      variant="outline"
                    >
                      <X className="h-4 w-4" />
                      Cancel
                    </Button>
                    {hasImageChanges && (
                      <Button
                        onClick={handleSaveImages}
                        size="sm"
                        variant="default"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Save
                      </Button>
                    )}
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isEditingImages ? (
                <div className="space-y-4">
                  <ImageUpload
                    field={{
                      onChange: handleImagesChange,
                      value: imageUrls,
                      name: 'product-images',
                      onBlur: () => {},
                      ref: () => {},
                    }}
                    maxImages={10}
                    enableReordering={true}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-4">
                  {images.map((image: any) => {
                    const isMarked = categoryPhotoId === image.id
                    const productKind: 'single' | 'advanced' = product.advanced_product ? 'advanced' : 'single'
                    const productChildId = product.advanced_product?.id ?? product.single_product?.id
                    return (
                      <div key={image.id} className="relative group">
                        <img
                          src={image.src_thumbnail || image.src}
                          alt={`${name} - Thumbnail`}
                          className="w-full h-32 object-cover rounded-md"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setCategoryPhoto({
                              variables: {
                                input: {
                                  [productKind === 'advanced' ? 'advancedProductId' : 'singleProductId']: productChildId,
                                  imageId: isMarked ? null : image.id,
                                },
                              },
                            })
                          }
                          className="absolute top-1 right-1 p-1 rounded bg-white/80 hover:bg-white opacity-0 group-hover:opacity-100 data-[marked=true]:opacity-100"
                          data-marked={isMarked}
                          title={isMarked ? 'Remove as category photo' : 'Set as category photo'}
                        >
                          <Star className={`h-4 w-4 ${isMarked ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400 hover:text-yellow-300'}`} />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>
          {product.type === 'ADVANCED_PRODUCT' &&
            product.advanced_product?.fabricCombinations &&
            product.advanced_product.fabricCombinations.length > 0 && (
              <FabricCombinationsCard
                fabricCombinations={product.advanced_product.fabricCombinations}
                language={language}
                advancedProductId={product.advanced_product.id}
              />
            )}

          {product.type === 'ADVANCED_PRODUCT' &&
            product.advanced_product &&
            product.advanced_product.advanced_product_type === 'SOFA' && (
              <SofaFormsTable
                productId={Number(id)}
                initialCount={product.advanced_product._count?.sofa_forms ?? 0}
                advancedProductId={product.advanced_product.id}
              />
            )}

          {product.type === 'ADVANCED_PRODUCT' && product.advanced_product && (
            <>
              <AdditionalComponentsCard
                advancedProduct={product.advanced_product}
                language={language}
                manufacturerId={product.manufacturerId}
                containerProductId={Number(id)}
              />
              <PackageDimensionsCard
                package_dimensions={product.advanced_product.package_dimensions}
              />
              <ProductPricelistsCard
                productId={Number(id)}
                language={language}
              />
            </>
          )}
        </div>

        <div className="space-y-8">
          <TableCard
            title="Organize"
            data={[
              {
                label: 'Visible',
                value: (
                  <Switch
                    checked={product.visible}
                    onCheckedChange={handleVisibilityChange}
                  />
                ),
              },
              { label: 'Type', value: product.type },
              ...(product.type === 'ADVANCED_PRODUCT' && product.advanced_product
                ? [{
                    label: 'Furniture Type',
                    value: (
                      <Select
                        value={product.advanced_product.advanced_product_type}
                        onValueChange={handleFurnitureTypeChange}
                      >
                        <SelectTrigger className="h-7 w-[180px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FURNITURE_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {FURNITURE_TYPE_LABELS[type]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ),
                  }]
                : []),
              {
                label: 'Manufacturer',
                value:
                  product.manufacturer?.manufacturer_profiles?.find(
                    (profile: any) =>
                      profile.language.toLowerCase() === language
                  )?.name ||
                  product.manufacturer?.manufacturer_profiles?.[0]?.name ||
                  '-',
              },
              {
                label: 'Categories',
                value: (
                  <div className="flex items-center gap-2">
                    <div className="flex flex-wrap gap-2 text-gray-400 flex-1">
                      {product.categories?.map((category: any) => (
                        <Badge
                          key={category.id}
                          variant={category.id === product.primary_categoryId ? 'green' : 'lightBlue'}
                        >
                          {category.category_profiles.find(
                            (profile: any) =>
                              profile.language.toLowerCase() === language
                          )?.name || category.category_profiles[0]?.name}
                        </Badge>
                      ))}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsCategoryModalOpen(true)}
                      className="h-6 w-6 p-0"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </div>
                ),
              },
              {
                label: 'Collections',
                value: (
                  <div className="flex items-start gap-2">
                    <div className="space-y-2 flex-1">
                      <div className="flex flex-wrap gap-2 text-gray-400">
                        {product.collection
                          ?.slice(0, showAllCollections ? undefined : 8)
                          .map((collection: any) => (
                            <Badge key={collection.id} variant="lightBlue">
                              {collection.collection_profiles?.find(
                                (profile: any) =>
                                  profile.language.toLowerCase() === language
                              )?.name ||
                                collection.collection_profiles?.[0]?.name}
                            </Badge>
                          ))}
                      </div>
                      {product.collection && product.collection.length > 8 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setShowAllCollections(!showAllCollections)
                          }
                          className="text-xs text-gray-500 hover:text-gray-700 p-1 h-auto"
                        >
                          {showAllCollections
                            ? `Show less (${
                                product.collection.length - 8
                              } hidden)`
                            : `Show ${product.collection.length - 8} more`}
                        </Button>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsCollectionModalOpen(true)}
                      className="h-6 w-6 p-0"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </div>
                ),
              },
              {
                label: 'Tags',
                value: (
                  <div className="flex items-center gap-2">
                    <div className="flex flex-wrap gap-2 text-gray-400 flex-1">
                      {product.tags.map((tag: any) => (
                        <Badge key={tag.id} variant="lightBlue">
                          {tag.tag_profiles.find(
                            (profile: any) =>
                              profile.language.toLowerCase() === language
                          )?.name || tag.tag_profiles[0]?.name}
                        </Badge>
                      ))}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsTagModalOpen(true)}
                      className="h-6 w-6 p-0"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </div>
                ),
              },
            ]}
          />
          <ProductFeaturesCard
            productId={Number(id)}
            assignedFeatures={product.product_features || []}
            language={language}
          />
          <ProductAttributesCard
            productId={Number(id)}
            assignedAttributes={product.product_attributes || []}
            language={language}
          />
          <LinkedProductsCard
            productId={Number(id)}
            linkedProducts={product.linked_products_as_source || []}
            language={language}
          />
        </div>
      </div>

      <CategoryEditModal
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        productId={Number(id)}
        currentCategoryIds={product.categories?.map((c: any) => c.id) || []}
        currentPrimaryCategoryId={product.primary_categoryId ?? null}
        language={language}
      />

      <TagEditModal
        isOpen={isTagModalOpen}
        onClose={() => setIsTagModalOpen(false)}
        productId={Number(id)}
        currentTagIds={product.tags?.map((t: any) => t.id) || []}
        language={language}
      />

      <CollectionEditModal
        isOpen={isCollectionModalOpen}
        onClose={() => setIsCollectionModalOpen(false)}
        productId={Number(id)}
        currentCollectionIds={product.collection?.map((c: any) => c.id) || []}
        language={language}
      />
    </PageContent>
  )
}
